import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Utensils, Coffee, Building2, Palmtree, Cross, Wheat, Shirt, ChefHat,
  ShoppingCart, Cloud, Factory, Crown, ArrowRight, ArrowLeft
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES } from "../lib/i18n";
import api from "../lib/api";
import { useSiteImages } from "../hooks/use-site-images";

const ICONS = {
  restaurant: Utensils, cafe: Coffee, hotel: Building2, resort: Palmtree,
  villa_palace: Crown,
  hospital: Cross, bakery: Wheat, laundry: Shirt, catering: ChefHat,
  supermarket: ShoppingCart, cloud_kitchen: Cloud, central_kitchen: Factory,
};

const SECTOR_IMAGES = {
  restaurant: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=600&q=75",
  cafe: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=600&q=75",
  hotel: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=600&q=75",
  resort: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=600&q=75",
  villa_palace: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=75",
  hospital: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=600&q=75",
  bakery: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=600&q=75",
  laundry: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=600&q=75",
  catering: "https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&w=600&q=75",
  supermarket: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=600&q=75",
  cloud_kitchen: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=600&q=75",
  central_kitchen: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=600&q=75",
};

const BLURB_EN = {
  restaurant: "Full show kitchens, prep lines and stainless fabrication.",
  cafe: "Espresso platforms, pastry displays and compact prep areas.",
  hotel: "Main production, banquet, room service and outlet kitchens.",
  resort: "Multi-outlet kitchens, pool bars and central production.",
  villa_palace: "Luxury private kitchens for villas, palaces and royal residences.",
  hospital: "HACCP-compliant patient meal production with diet-line separation.",
  bakery: "Deck ovens, dough mixers, proofers and cooling racks.",
  laundry: "Washers, tumble dryers, flatwork ironers and folding lines.",
  catering: "Central production kitchens with blast chillers and packaging.",
  supermarket: "Refrigerated display cases, walk-in cold rooms, meat prep.",
  cloud_kitchen: "Standardized pods, shared cold storage and delivery staging.",
  central_kitchen: "High-volume production, cook-chill and distribution logistics.",
};
const BLURB_AR = {
  restaurant: "مطابخ عرض متكاملة وخطوط تحضير وتصنيع ستانلس.",
  cafe: "منصات إسبريسو ومنضدات عرض ومناطق تحضير مدمجة.",
  hotel: "مطبخ إنتاج رئيسي وحفلات وخدمة غرف ومطاعم.",
  resort: "مطابخ متعددة المنافذ وبارات مسبح وإنتاج مركزي.",
  villa_palace: "مطابخ فاخرة خاصة للفلل والقصور والإقامات الملكية.",
  hospital: "إنتاج وجبات مرضى مطابق لـHACCP مع فصل خطوط الأنظمة.",
  bakery: "أفران طبقات وعجانات ومخمرات ورفوف تبريد.",
  laundry: "غسالات ومجففات ومكاوي مسطحة وخطوط طي.",
  catering: "مطابخ إنتاج مركزية مع مبردات صدمة وخطوط تعبئة.",
  supermarket: "منضدات عرض مبردة وغرف تبريد وتجميد وتجهيز لحوم.",
  cloud_kitchen: "وحدات موحدة وتخزين تبريد مشترك ومنطقة تجهيز التوصيل.",
  central_kitchen: "إنتاج عالي، وتوزيع لوجستي، وطهي-تبريد.",
};

export default function Sectors() {
  const { t, lang, isRTL } = useLang();
  const siteImage = useSiteImages();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [projects, setProjects] = useState([]);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data || [])); }, []);
  const byCategory = useMemo(() => {
    const map = {};
    for (const c of CATEGORIES) map[c] = [];
    for (const p of projects) if (map[p.category]) map[p.category].push(p);
    return map;
  }, [projects]);

  return (
    <div data-testid="sectors-page">
      {/* Hero (lighter) */}
      <section className="relative py-20 bg-gradient-to-br from-cyan-50 via-white to-red-50/30 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-4 font-semibold uppercase">
            {lang === "ar" ? "القطاعات التي نخدمها" : "SECTORS WE SERVE"}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-[#0891b2] max-w-3xl" data-testid="sectors-title">
            {lang === "ar" ? "خبرة قطاعية في كل نوع من مشاريع خدمات الطعام" : "Sector expertise across every food-service project type"}
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl">
            {lang === "ar"
              ? "لكل قطاع صفحته المخصصة مع أعمالنا السابقة وحلول مصممة لطبيعة عمله."
              : "Each sector has its own dedicated page with our past work and tailored solutions."}
          </p>
        </div>
      </section>

      {/* Sector cards with images */}
      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c) => {
            const Icon = ICONS[c] || Utensils;
            const blurb = lang === "ar" ? BLURB_AR[c] : BLURB_EN[c];
            const count = byCategory[c]?.length || 0;
            return (
              <Link
                key={c}
                to={`/sectors/${c}`}
                data-testid={`sector-${c}`}
                className="group bg-white border border-slate-200 hover:border-[#0891b2] hover:shadow-xl rounded-2xl overflow-hidden transition-all flex flex-col"
              >
                {/* Small image at top */}
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={siteImage(`sector_${c}`, SECTOR_IMAGES[c])}
                    alt={t.categories[c]}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/50 via-transparent to-transparent" />
                  <div className="absolute top-3 start-3 w-10 h-10 rounded-lg bg-white/95 backdrop-blur flex items-center justify-center shadow-md">
                    <Icon className="w-5 h-5 text-[#0891b2]" />
                  </div>
                  {count > 0 && (
                    <span className="absolute top-3 end-3 inline-flex items-center gap-1 bg-[#ef4444] text-white text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-widest">
                      {count} {lang === "ar" ? "مشروع" : count === 1 ? "project" : "projects"}
                    </span>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display text-lg text-slate-900 mb-2 group-hover:text-[#0891b2] transition-colors">{t.categories[c]}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1">{blurb}</p>
                  <div className="inline-flex items-center gap-1.5 text-[#ef4444] text-xs font-semibold uppercase tracking-widest">
                    {lang === "ar" ? "عرض الصفحة" : "View sector"}
                    <Arrow className="w-3.5 h-3.5 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
